
import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { getResponsiveImageSizes } from '@/utils/imageOptimizer';

interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  placeholderSrc?: string;
  priority?: boolean;
  blurhash?: string; // Optional blur hash for better placeholder
}

export function OptimizedImage({
  src,
  alt,
  width,
  height,
  className,
  placeholderSrc = '/placeholder.svg',
  priority = false,
  blurhash,
  ...props
}: OptimizedImageProps) {
  const [imgSrc, setImgSrc] = useState<string>(placeholderSrc || src);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  
  // Get optimal image dimensions based on device capabilities
  const { width: optimalWidth, quality } = getResponsiveImageSizes();

  // Function to handle image load error
  const handleError = () => {
    console.error(`Failed to load image: ${src}`);
    setHasError(true);
    
    // Register for background sync to try again when online
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      navigator.serviceWorker.ready
        .then(registration => {
          // Store the URL to retry in IndexedDB or other storage
          // This would normally be implemented with IndexedDB
          localStorage.setItem(`failed-img-${Date.now()}`, src);
          
          // Register for background sync
          // Need to cast registration to include the sync property
          // as it might not be in the TypeScript definitions
          const reg = registration as any;
          if (reg.sync) {
            return reg.sync.register('sync-images');
          }
          return Promise.resolve();
        })
        .catch(err => console.error('Error registering for sync:', err));
    }
  };

  useEffect(() => {
    // Reset loading state when src changes
    if (src !== imgSrc && isLoaded) {
      setIsLoaded(false);
      setImgSrc(placeholderSrc || src);
      setHasError(false);
    }

    // For priority images, load immediately without intersection observer
    if (priority) {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        setImgSrc(src);
        setIsLoaded(true);
      };
      img.onerror = handleError;

      return () => {
        img.onload = null;
        img.onerror = null;
      };
    }

    // For non-priority images, use intersection observer with more aggressive thresholds
    if (!priority && imgRef.current && 'IntersectionObserver' in window) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const img = new Image();
              img.src = src;
              img.onload = () => {
                setImgSrc(src);
                setIsLoaded(true);
                observerRef.current?.unobserve(entry.target);
              };
              img.onerror = () => {
                handleError();
                observerRef.current?.unobserve(entry.target);
              };
            }
          });
        },
        { 
          rootMargin: '300px 0px',  // Increased root margin to start loading earlier
          threshold: 0.01 
        }
      );
      
      observerRef.current.observe(imgRef.current);
    }

    return () => {
      if (observerRef.current && imgRef.current) {
        observerRef.current.unobserve(imgRef.current);
        observerRef.current.disconnect();
      }
    };
  }, [src, placeholderSrc, imgSrc, isLoaded, priority]);

  // If no alt text is provided, use a warning in development but empty in production
  const accessibleAlt = alt || (import.meta.env.DEV ? 'Image missing alt text' : '');

  // Generate srcset for responsive images if this is an external image
  // This doesn't work for data URIs, but would help with actual hosted images
  const getSrcSet = () => {
    if (src.startsWith('data:') || src.startsWith('blob:')) return undefined;
    
    // For static images, create a responsive srcset
    if (src.startsWith('http') || src.startsWith('/')) {
      return `${src} 1x, ${src} 2x`;
    }
    
    return undefined;
  };

  return (
    <div className={cn(
      'relative overflow-hidden',
      className
    )}>
      {/* Show blurred placeholder if available */}
      {!isLoaded && blurhash && (
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat z-0 scale-110 blur-xl"
          style={{ 
            backgroundImage: `url(${blurhash})`,
            opacity: 0.7
          }}
          aria-hidden="true"
        />
      )}
      
      <img
        ref={imgRef}
        src={imgSrc}
        alt={accessibleAlt}
        width={width}
        height={height}
        srcSet={getSrcSet()}
        className={cn(
          'w-full h-auto transition-opacity duration-300 relative z-10',
          isLoaded ? 'opacity-100' : 'opacity-40',
          hasError ? 'opacity-40 grayscale' : ''
        )}
        loading={priority ? 'eager' : 'lazy'}
        decoding={priority ? 'sync' : 'async'}
        onError={handleError}
        {...props}
      />
      
      {/* Show error indicator for failed images */}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100/50 z-20">
          <span className="text-sm text-gray-500">Image failed to load</span>
        </div>
      )}
    </div>
  );
}
